import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Space, Spin, Typography } from 'antd';
import { useDataFilter } from '../../../hooks/SimViewer/useDataFilter';
import { useTripHistogramData } from '../../../hooks/data/useTripHistogramData';
import { isSameRange } from '../../../utils/range';
import { formatTime } from '../../../utils/format';
import { FilterIcon } from '../../FilterIcon';
import { HistogramChart } from '../Chart/HistogramChart';

type Props = {
  icon: ReactNode;
  label: string;
  extra?: ReactNode;
};

const PassageTimePanel = ({
  icon,
  label,
  extra,
  ...props
}: Props): ReactNode => {
  const { dataFilter } = useDataFilter();

  return (
    <Collapse.Panel
      key="passage_time"
      header={
        <Space>
          {icon}
          {label}
          {extra}
          {dataFilter.passageTime && <FilterIcon />}
        </Space>
      }
      {...props}
    >
      <PassageTimePanelContent />
    </Collapse.Panel>
  );
};

const PassageTimePanelContent = (): ReactNode => {
  const { t } = useTranslation();
  const { dataFilter, setRangeFilter } = useDataFilter();
  const { isLoading, mean, median, items } =
    useTripHistogramData('passage_time');

  return (
    <Spin spinning={isLoading}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space>
          <Typography.Text>
            {t('mean')}：{mean !== null ? formatTime(mean) : '-'}
          </Typography.Text>
          <Typography.Text>
            {t('median')}：{median !== null ? formatTime(median) : '-'}
          </Typography.Text>
        </Space>
        <HistogramChart
          data={items}
          rangeType="time"
          unit={t('unit.agent')}
          noDataMessage={t('message.passageTimeUnavailableDueToConditions')}
          filterRange={dataFilter.passageTime}
          onFilterRangeChange={(filterRange: [number, number] | null): void => {
            if (!isSameRange(filterRange, dataFilter.passageTime)) {
              setRangeFilter('passageTime', filterRange);
            }
          }}
        />
      </Space>
    </Spin>
  );
};

export { PassageTimePanel };
